import { FC } from 'react';
import { Globe } from 'lucide-react';
import { Button } from '@new/common/ui/Button/Button';
import { CancelButton } from '@new/common/ui/CancelButton/CancelButton';
import { formatDate, formatTime, timestampToDate } from '@new/common/lib/dateTime';
import { getUserTimezone, getTimezoneOffset } from '@new/features/schedule/lib/helpers';
import { ScheduledCall } from '@teachur/http-client';
import { CancelScheduledCallAction } from './CancelScheduledCallAction';

interface ScheduleCardProps {
  call: ScheduledCall;
  onJoinClick?: (callId: string) => void;
  canCancel?: boolean;
  showStatus?: boolean;
  overrideStatus?: 'completed' | 'missed';
}

const STATUS_CONFIG = {
  pending: {
    label: 'Pending',
    backgroundColor: 'bg-(--warning-W100)',
    textColor: 'text-(--warning-W700)',
  },
  accepted: {
    label: 'Accepted',
    backgroundColor: 'bg-(--success-S200)',
    textColor: 'text-(--success-S700)',
  },
  rejected: {
    label: 'Rejected',
    backgroundColor: 'bg-(--danger-D200)',
    textColor: 'text-(--danger-D700)',
  },
  completed: {
    label: 'Completed',
    backgroundColor: 'bg-(--neutral-black-NB200)',
    textColor: 'text-(--neutral-black-NB700)',
  },
  missed: {
    label: 'Missed',
    backgroundColor: 'bg-(--danger-D200)',
    textColor: 'text-(--danger-D700)',
  },
} as const;

export const ScheduleCard: FC<ScheduleCardProps> = ({
  call,
  onJoinClick,
  canCancel = false,
  showStatus = true,
  overrideStatus,
}) => {
  const callStartDate = timestampToDate(call.startTime);
  const callEndDate = new Date(callStartDate);
  callEndDate.setHours(callEndDate.getHours() + 1);
  const statusConfig = STATUS_CONFIG[overrideStatus || (call.status as keyof typeof STATUS_CONFIG)];
  const userTimezone = getUserTimezone();
  const timezoneOffset = getTimezoneOffset();

  return (
    <div className="flex flex-col gap-6 p-5 border border-(--neutral-black-NB200) rounded-[12px] bg-[#FAFDFF] hover:shadow-md transition-shadow">
      <div className="flex flex-col gap-5">
        <div className="flex items-center justify-between">
          {/* Title TODO: implement when backend is finished */}
          <h4 className="[font-size:var(--fs-p2)]! text-(--neutral-black-NB1000)! font-semibold m-0! leading-[150%]">
            Course hearing on {call.course?.name || 'History of Psychology'}
          </h4>
          {showStatus &&
            statusConfig &&
            overrideStatus !== 'completed' &&
            (overrideStatus === 'missed' || call.status !== 'accepted') && (
              <span
                className={`flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusConfig.backgroundColor} ${statusConfig.textColor}`}
              >
                {statusConfig.label}
              </span>
            )}
        </div>
        <div className="flex flex-col gap-0.5 self-start border border-(--neutral-black-NB200) bg-(--neutral-white-NW1000) rounded-2xl px-2.5 py-1.5 [font-size:var(--fs-l5)]! text-(--neutral-black-NB700)!">
          <span className="flex items-center gap-1">
            {formatDate(timestampToDate(call.startTime))}
            <div className="w-1.5 h-1.5 bg-(--neutral-black-NB300) rounded-full" />
            {formatTime(timestampToDate(call.startTime))} - {formatTime(callEndDate)}
          </span>
          <span className="flex items-center gap-1 text-(--neutral-black-NB500)!">
            <Globe className="h-3 w-3" />
            {userTimezone} ({timezoneOffset})
          </span>
        </div>
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        {canCancel && (
          <CancelScheduledCallAction call={call}>
            <CancelButton text="Cancel" />
          </CancelScheduledCallAction>
        )}
        {onJoinClick && call.videoCallId && (
          <Button
            variant="primary"
            onClick={() => onJoinClick(call.videoCallId!)}
            text="Join now"
          />
        )}
      </div>
    </div>
  );
};
